import { useEffect, useState, useCallback } from "react";
import { Plus, Trash2, X, Save, Tag } from "lucide-react";
import { toast } from "sonner";
import {
  adminListDiscounts, adminCreateDiscount, adminUpdateDiscount, adminDeleteDiscount,
  adminGetReadyMadeSale, adminPutReadyMadeSale,
} from "../../lib/api";
import { AField } from "./shared";
import { invalidateReadyMadeSale } from "../../hooks/useReadyMadeSale";

export function DiscountsTab({ pw }) {
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const reload = useCallback(() => { adminListDiscounts(pw).then(setItems); }, [pw]);
  useEffect(reload, [reload]);

  const toggle = async (d) => {
    try { await adminUpdateDiscount(d.id, { active: !d.active }, pw); reload(); }
    catch { toast.error("Update failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete code?")) return;
    try { await adminDeleteDiscount(id, pw); reload(); toast.success("Deleted"); }
    catch { toast.error("Delete failed"); }
  };

  // Sort manual codes before auto-generated cart-recovery codes so the
  // admin's hand-curated promos aren't buried under per-customer codes.
  const sorted = [...items].sort((a, b) => {
    if ((a.auto_generated ? 1 : 0) !== (b.auto_generated ? 1 : 0)) {
      return (a.auto_generated ? 1 : 0) - (b.auto_generated ? 1 : 0);
    }
    return (b.created_at || "").localeCompare(a.created_at || "");
  });

  const fmtValue = (d) => {
    if (d.type === "percent") return `${d.value}%`;
    if (d.type === "free_shipping") return "FREE SHIP";
    return `$${d.value}`;
  };

  const fmtExpiry = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      const days = Math.round((d - Date.now()) / (1000 * 60 * 60 * 24));
      if (days < 0) return <span className="text-red-400">expired</span>;
      if (days === 0) return <span className="text-amber-400">today</span>;
      if (days <= 3) return <span className="text-amber-400">{days}d</span>;
      return <span className="text-zinc-400">{d.toLocaleDateString()}</span>;
    } catch { return "—"; }
  };

  return (
    <>
      <ReadyMadeSaleCard pw={pw} />
      <div className="flex justify-end mb-4">
        <button data-testid="create-discount-btn" onClick={() => setCreating(true)} className="btn-tc-primary flex items-center gap-2 py-3 px-5">
          <Plus size={16} /> Add Code
        </button>
      </div>
      <div className="surface-card overflow-x-auto" data-testid="discounts-grid">
        <table className="w-full text-left">
          <thead className="bg-[#050505] border-b border-[#222]">
            <tr className="font-mono text-[10px] tracking-widest text-zinc-500">
              <th className="p-4">CODE</th><th className="p-4">TYPE</th><th className="p-4">VALUE</th>
              <th className="p-4">MIN $</th><th className="p-4">USED</th>
              <th className="p-4">EXPIRES</th><th className="p-4">FLAGS</th>
              <th className="p-4">STATUS</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((d) => (
              <tr key={d.id} className={`border-b border-[#222] ${d.auto_generated ? "opacity-60" : ""}`} data-testid={`discount-${d.id}`}>
                <td className="p-4 font-mono font-bold">
                  {d.code}
                  {d.description && (
                    <div className="font-mono text-[10px] text-zinc-500 normal-case font-normal mt-1">{d.description}</div>
                  )}
                </td>
                <td className="p-4 font-mono text-xs">{d.type}</td>
                <td className="p-4 font-mono text-[var(--tc-red)]">{fmtValue(d)}</td>
                <td className="p-4 font-mono text-xs">${(d.min_subtotal || 0).toFixed(2)}</td>
                <td className="p-4 font-mono text-xs">
                  {d.usage_count || 0} / {d.max_uses || "∞"}
                  {d.max_uses_per_user ? (
                    <div className="font-mono text-[9px] text-zinc-500">{d.max_uses_per_user}/customer</div>
                  ) : null}
                </td>
                <td className="p-4 font-mono text-xs">{fmtExpiry(d.expires_at)}</td>
                <td className="p-4 font-mono text-[10px]">
                  {d.first_time_customer_only && <div className="text-emerald-400">FIRST-TIME ONLY</div>}
                  {d.auto_generated && <div className="text-zinc-500">auto-recovery</div>}
                </td>
                <td className="p-4">
                  <button onClick={() => toggle(d)} className={`chip ${d.active ? "border-green-700 text-green-400" : "border-zinc-700 text-zinc-500"}`}>
                    {d.active ? "ACTIVE" : "OFF"}
                  </button>
                </td>
                <td className="p-4">
                  <button data-testid={`delete-discount-${d.id}`} onClick={() => remove(d.id)} className="text-zinc-400 hover:text-[var(--tc-red)]"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {creating && (
        <DiscountForm
          onClose={() => setCreating(false)}
          onSave={async (data) => {
            try { await adminCreateDiscount(data, pw); reload(); setCreating(false); toast.success("Created"); }
            catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
          }}
        />
      )}
    </>
  );
}

export function DiscountForm({ onClose, onSave }) {
  const [form, setForm] = useState({
    code: "",
    type: "percent",
    value: 10,
    min_subtotal: 0,
    max_uses: "",
    max_uses_per_user: "",
    expires_at: "",
    first_time_customer_only: false,
    description: "",
  });
  const on = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const onCheckbox = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.checked }));

  return (
    <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="surface-card max-w-lg w-full my-8" data-testid="discount-form">
        <div className="p-6 border-b border-[#222] flex justify-between items-center">
          <h3 className="font-display text-2xl uppercase">New Discount Code</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          <AField label="Code (e.g. SUMMER20)" value={form.code} onChange={on("code")} fullWidth />
          <div>
            <label className="font-mono text-[10px] tracking-widest text-zinc-500">TYPE</label>
            <select value={form.type} onChange={on("type")} className="w-full bg-black border border-[#222] px-3 py-2 mt-1 font-body text-sm" data-testid="discount-type">
              <option value="percent">Percent (%)</option>
              <option value="fixed">Fixed (CAD)</option>
              <option value="free_shipping">Free Shipping</option>
            </select>
          </div>
          {form.type !== "free_shipping" && (
            <AField label={form.type === "percent" ? "Value (%)" : "Value (CAD)"} type="number" value={form.value} onChange={on("value")} fullWidth />
          )}
          <AField label="Min Subtotal (CAD)" type="number" value={form.min_subtotal} onChange={on("min_subtotal")} fullWidth />
          <div className="grid grid-cols-2 gap-3">
            <AField label="Max Uses (blank = ∞)" type="number" value={form.max_uses} onChange={on("max_uses")} fullWidth />
            <AField label="Max per Customer" type="number" value={form.max_uses_per_user} onChange={on("max_uses_per_user")} fullWidth />
          </div>
          <div>
            <label className="font-mono text-[10px] tracking-widest text-zinc-500">EXPIRES (BLANK = NEVER)</label>
            <input
              type="date" value={form.expires_at} onChange={on("expires_at")}
              data-testid="discount-expires"
              className="w-full bg-black border border-[#222] px-3 py-2 mt-1 font-body text-sm focus:border-[var(--tc-red)] outline-none [color-scheme:dark]"
            />
          </div>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox" checked={form.first_time_customer_only}
              onChange={onCheckbox("first_time_customer_only")}
              data-testid="discount-first-time"
              className="mt-1"
            />
            <span className="font-mono text-xs text-zinc-300">
              First-time customers only
              <span className="block text-[10px] text-zinc-500 mt-0.5">Only valid for emails with no prior paid orders.</span>
            </span>
          </label>
          <AField label="Internal description (optional)" value={form.description} onChange={on("description")} fullWidth />
        </div>
        <div className="p-6 border-t border-[#222] flex justify-end gap-3">
          <button onClick={onClose} className="btn-tc-secondary">Cancel</button>
          <button
            data-testid="save-discount-btn"
            onClick={() => onSave({
              code: form.code.toUpperCase(),
              type: form.type,
              value: form.type === "free_shipping" ? 0 : (parseFloat(form.value) || 0),
              min_subtotal: parseFloat(form.min_subtotal) || 0,
              max_uses: form.max_uses ? parseInt(form.max_uses) : null,
              max_uses_per_user: form.max_uses_per_user ? parseInt(form.max_uses_per_user) : null,
              expires_at: form.expires_at ? new Date(form.expires_at + "T23:59:59Z").toISOString() : null,
              first_time_customer_only: !!form.first_time_customer_only,
              description: form.description || "",
              active: true,
            })}
            className="btn-tc-primary"
          >Save</button>
        </div>
      </div>
    </div>
  );
}

// ===== QUOTES =====

export default DiscountsTab;

// ---------------------------------------------------------------------------
// iter73 — Ready-Made global sale toggle
// ---------------------------------------------------------------------------
// A single global percentage that slashes every Ready-Made product's price
// site-wide. Persisted to db.site_settings via /api/admin/site/sale.
function ReadyMadeSaleCard({ pw }) {
  const [state, setState] = useState({ active: false, pct: 0, label: "", endsAt: "" });
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    adminGetReadyMadeSale(pw)
      .then((s) => setState({
        active: !!s?.active,
        pct: Number(s?.pct) || 0,
        label: s?.label || "",
        // Convert stored UTC ISO to a `datetime-local` compatible string
        // ("YYYY-MM-DDTHH:MM") in the browser's local TZ so the input
        // shows what the admin expects to see.
        endsAt: s?.ends_at
          ? (() => {
              try {
                const d = new Date(s.ends_at);
                const off = d.getTimezoneOffset() * 60000;
                return new Date(d - off).toISOString().slice(0, 16);
              } catch { return ""; }
            })()
          : "",
      }))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, [pw]);

  const save = async (patch) => {
    setSaving(true);
    try {
      // Convert local datetime-local back to ISO UTC before sending.
      const body = { ...state, ...patch };
      const endsLocal = body.endsAt;
      body.ends_at = endsLocal ? new Date(endsLocal).toISOString() : null;
      delete body.endsAt;
      const next = await adminPutReadyMadeSale(pw, body);
      setState({
        active: !!next.active,
        pct: Number(next.pct) || 0,
        label: next.label || "",
        endsAt: next.ends_at
          ? (() => {
              try {
                const d = new Date(next.ends_at);
                const off = d.getTimezoneOffset() * 60000;
                return new Date(d - off).toISOString().slice(0, 16);
              } catch { return ""; }
            })()
          : "",
      });
      invalidateReadyMadeSale();      // clear cache so PDP/cards refetch
      toast.success(
        next.active
          ? `Ready-Made sale live at ${next.pct}% off${next.ends_at ? ` until ${new Date(next.ends_at).toLocaleString()}` : ""}`
          : "Ready-Made sale paused"
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) {
    return (
      <div className="surface-card p-5 mb-6 font-mono text-[11px] text-zinc-500">
        Loading Ready-Made sale…
      </div>
    );
  }

  return (
    <div className="surface-card p-5 mb-6" data-testid="ready-made-sale-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Tag size={14} className="text-emerald-400" />
            <span className="font-mono text-[11px] tracking-widest text-zinc-400 uppercase">
              Ready-Made Site Sale
            </span>
            {state.active && (
              <span
                className="font-mono text-[10px] px-2 py-0.5 bg-emerald-500 text-black tracking-widest"
                data-testid="rm-sale-live-badge"
              >
                LIVE · {state.pct}% OFF
              </span>
            )}
          </div>
          <div className="font-body text-xs text-zinc-500 mt-2 max-w-xl">
            Applies to every Ready-Made design site-wide. Shows a slashed
            &ldquo;was $X&rdquo; price on the PDP, grid cards, and cart
            line-items. Checkout auto-applies the discount server-side.
          </div>
        </div>
        <button
          onClick={() => save({ active: !state.active })}
          disabled={saving || (!state.active && state.pct <= 0)}
          data-testid="rm-sale-toggle-btn"
          className={`px-4 py-2 font-mono text-xs tracking-widest border transition ${
            state.active
              ? "border-red-500 text-red-400 hover:bg-red-500/10"
              : "border-emerald-500 text-emerald-400 hover:bg-emerald-500/10 disabled:opacity-40"
          }`}
        >
          {saving ? "SAVING…" : state.active ? "PAUSE SALE" : "GO LIVE"}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5">
        <div>
          <label className="font-mono text-[10px] tracking-widest text-zinc-500 block mb-1">
            DISCOUNT %
          </label>
          <div className="flex gap-2 items-center">
            <input
              type="number"
              min={0}
              max={90}
              step={1}
              value={state.pct}
              onChange={(e) =>
                setState((s) => ({ ...s, pct: Math.max(0, Math.min(90, Number(e.target.value) || 0)) }))
              }
              onBlur={() => save({ pct: state.pct })}
              data-testid="rm-sale-pct-input"
              className="bg-black border border-[#222] px-3 py-2 font-mono text-sm w-24 text-white"
            />
            <span className="font-mono text-xs text-zinc-500">% off</span>
          </div>
        </div>
        <div>
          <label className="font-mono text-[10px] tracking-widest text-zinc-500 block mb-1">
            LABEL (OPTIONAL)
          </label>
          <input
            type="text"
            placeholder="SUMMER SALE"
            maxLength={64}
            value={state.label}
            onChange={(e) => setState((s) => ({ ...s, label: e.target.value }))}
            onBlur={() => save({ label: state.label })}
            data-testid="rm-sale-label-input"
            className="bg-black border border-[#222] px-3 py-2 font-mono text-sm w-full text-white"
          />
        </div>
        <div>
          <label className="font-mono text-[10px] tracking-widest text-zinc-500 block mb-1">
            ENDS AT (OPTIONAL)
          </label>
          <div className="flex gap-2 items-center">
            <input
              type="datetime-local"
              value={state.endsAt}
              onChange={(e) => setState((s) => ({ ...s, endsAt: e.target.value }))}
              onBlur={() => save({ endsAt: state.endsAt })}
              data-testid="rm-sale-ends-at-input"
              className="bg-black border border-[#222] px-3 py-2 font-mono text-xs text-white flex-1"
            />
            {state.endsAt && (
              <button
                type="button"
                onClick={() => { setState((s) => ({ ...s, endsAt: "" })); save({ endsAt: "" }); }}
                data-testid="rm-sale-ends-at-clear"
                className="font-mono text-[10px] text-zinc-400 hover:text-white px-2"
                title="Clear end time"
              >
                CLEAR
              </button>
            )}
          </div>
          <div className="font-mono text-[9px] text-zinc-500 mt-1">
            Sale auto-pauses when this time passes.
          </div>
        </div>
      </div>
    </div>
  );
}
